/**
 * Combien de commandes y a-t-il à préparer AUJOURD'HUI ?
 *
 * C'est le chiffre du badge « 📦 Prépa » de la caisse. Il ne comptait
 * jusqu'ici que les statuts `confirmed` / `preparing`, sans regarder AUCUNE
 * date : une commande eshop pour le samedi allumait le badge dès le mercredi
 * (« 2 prépa » un jour où il n'y avait rien à faire, 02/09/2026), tandis que
 * les vingt commandes de pain du vendredi — toutes en `pending`, l'état normal
 * d'une précommande acceptée — ne comptaient pour rien.
 *
 * Deux règles, donc :
 *
 * 1. LA DATE. Une commande compte le jour où il y a quelque chose à faire,
 *    c'est-à-dire quand la marchandise est là : le jour de LIVRAISON de ses
 *    articles, pas le jour où le client passera la prendre. C'est déjà la
 *    logique des fiches de préparation (`generatePreparationSheets`) — un pain
 *    livré le vendredi pour un retrait le samedi se met de côté le vendredi.
 *    Le retard compte aussi : une commande d'hier non préparée reste du
 *    travail, elle ne doit jamais disparaître du compteur.
 *
 * 2. LE STATUT, qui ne veut pas dire la même chose des deux côtés :
 *    - commande eshop `pending` NON payée = panier abandonné avant le paiement
 *      (il part au cron des expirées) → ne pas compter ; mais `pending` PAYÉE
 *      = commande encaissée que rien n'a confirmée → à compter, sinon personne
 *      ne la prépare. Depuis le 10/09/2026 une commande réglée en FOLIES naît
 *      `confirmed` : ce cas ne concerne plus que les commandes d'avant, et
 *      resterait le filet d'un canal qui encaisserait sans confirmer ;
 *    - précommande `pending` = commande de pain acceptée → à compter.
 *    Les deux se normalisent en `'pending'` dans `/orders/preparation/list`,
 *    d'où la distinction par `_type`. `ready` = déjà préparée, elle attend son
 *    client : ce n'est plus du travail.
 */

export type EntreePreparation = {
  status?: string;
  /** `paid` = encaissée : c'est du travail, quel que soit le statut. */
  payment_status?: string;
  _type?: 'order' | 'preorder';
  /** Retrait (commande) ou reprise (précommande). */
  delivery_date?: string | null;
  /** Les lignes d'une précommande portent le jour où le producteur livre. */
  lines?: Array<{ delivery_date?: string | null }>;
};

/** Jour civil au format `YYYY-MM-DD`, en heure locale du poste. */
const jourLocal = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Le jour d'une date ISO, quelle que soit sa forme (`2026-09-05`, ISO complet). */
const jourDe = (v?: string | null): string | null => (v ? String(v).slice(0, 10) : null);

/**
 * Jour où cette commande demande du travail : le premier jour de livraison de
 * ses lignes, à défaut sa date de retrait. Une commande étalée sur deux
 * cuissons apparaît donc dès la première — il y a déjà quelque chose à mettre
 * de côté.
 */
export const jourDeTravail = (e: EntreePreparation): string | null => {
  const joursLignes = (e.lines || [])
    .map((l) => jourDe(l?.delivery_date))
    .filter((j): j is string => !!j);
  if (joursLignes.length > 0) return joursLignes.sort()[0];
  return jourDe(e.delivery_date);
};

export const estAPreparer = (e: EntreePreparation, aujourdhui: string): boolean => {
  const statut = (e.status || '').toLowerCase();

  const paye = (e.payment_status || '').toLowerCase() === 'paid';

  const enAttenteDeTravail =
    e._type === 'preorder'
      ? statut === 'pending' || statut === 'preparing'
      : statut === 'confirmed' ||
        statut === 'preparing' ||
        // Rattrapage d'historique : les commandes payées en FOLIES naissent
        // désormais `confirmed`, mais celles créées avant ce correctif restent
        // `pending` en base — elles n'apparaissaient JAMAIS au badge, et
        // personne ne les préparait avant que le client ne se présente. Ce qui
        // distingue le travail du panier abandonné, c'est le PAIEMENT, pas le
        // statut : cette branche reste donc, et couvrirait un canal futur qui
        // encaisserait sans confirmer.
        (statut === 'pending' && paye);
  if (!enAttenteDeTravail) return false;

  const jour = jourDeTravail(e);
  // Sans date, on préfère compter : une commande sans repère est justement
  // celle qu'on risque d'oublier.
  if (!jour) return true;

  return jour <= aujourdhui;
};

export const compterPreparationsDuJour = (
  entrees: EntreePreparation[],
  maintenant: Date = new Date(),
): number => {
  const aujourdhui = jourLocal(maintenant);
  return entrees.filter((e) => estAPreparer(e, aujourdhui)).length;
};
